/**
 * https://github.com/tldraw/tldraw/blob/daf729d45c879d4e234d9417570149ad854f635b/packages/tlschema/src/SWStore.ts
 *
 * Changes:
 *  - Removed redactRecordForErrorReporting
 *  - Editing some typing to make it work
 *
 */ import { annotateError } from "../utils";
import { DocumentRecordType, SWDOCUMENT_ID, SWINSTANCE_ID } from "./records";
import { SWInstancePageStateRecordType } from "./records/SWPageState";
import { CameraRecordType } from "./records/TLCamera";
import { PageRecordType } from "./records/TLPage";
import { PointerRecordType, TLPOINTER_ID } from "./records/TLPointer";
// import { SWStoreSchemaImpl, SWStoreSchemaImplOptions } from "@supersig/store"
// import { SWStoreImpl } from "@supersig/store"
function sortByIndex(a, b) {
    if (a.index < b.index) {
        return -1;
    } else if (a.index > b.index) {
        return 1;
    }
    return 0;
}
/** @public */ export const onValidationFailure = ({ error, phase, record, recordBefore })=>{
    const isExistingValidationIssue = // if we're initializing the store for the first time, we should
    // allow invalid records so people can load old buggy data:
    phase === "initialize";
    annotateError(error, {
        tags: {
            origin: "store.validateRecord",
            storePhase: phase,
            isExistingValidationIssue
        },
        extras: {
            recordBefore: recordBefore ? structuredClone(recordBefore) : undefined,
            recordAfter: structuredClone(record)
        }
    });
    throw error;
};
function getDefaultPages() {
    return [
        PageRecordType.create({
            id: "page:page",
            name: "Page 1",
            index: "a1",
            meta: {}
        })
    ];
}
/** @internal */ export function createIntegrityChecker(store) {
    const $pageIds = store.query.ids("page");
    const ensureStoreIsUsable = ()=>{
        // make sure we have exactly one document
        if (!store.has(SWDOCUMENT_ID)) {
            store.put([
                DocumentRecordType.create({
                    id: SWDOCUMENT_ID,
                    name: store.props.defaultName
                })
            ]);
            return ensureStoreIsUsable();
        }
        if (!store.has(TLPOINTER_ID)) {
            store.put([
                PointerRecordType.create({
                    id: TLPOINTER_ID
                })
            ]);
            return ensureStoreIsUsable();
        }
        // make sure there is at least one page
        const pageIds = $pageIds.get();
        if (pageIds.size === 0) {
            store.put(getDefaultPages());
            return ensureStoreIsUsable();
        }
        const getFirstPageId = ()=>[
                ...pageIds
            ].map((id)=>store.get(id)).sort(sortByIndex)[0].id;
        // make sure we have state for the current user's current tab
        const instanceState = store.get(SWINSTANCE_ID);
        if (!instanceState) {
            store.put([
                store.schema.types.instance.create({
                    id: SWINSTANCE_ID,
                    currentPageId: getFirstPageId(),
                    exportBackground: true
                })
            ]);
            return ensureStoreIsUsable();
        } else if (!pageIds.has(instanceState.currentPageId)) {
            store.put([
                {
                    ...instanceState,
                    currentPageId: getFirstPageId()
                }
            ]);
            return ensureStoreIsUsable();
        }
        // make sure we have page states and cameras for all the pages
        const missingPageStateIds = new Set();
        const missingCameraIds = new Set();
        for (const id of pageIds){
            const pageStateId = SWInstancePageStateRecordType.createId(id);
            if (!store.has(pageStateId)) {
                missingPageStateIds.add(pageStateId);
            }
            const cameraId = CameraRecordType.createId(id);
            if (!store.has(cameraId)) {
                missingCameraIds.add(cameraId);
            }
        }
        if (missingPageStateIds.size > 0) {
            store.put([
                ...missingPageStateIds
            ].map((id)=>SWInstancePageStateRecordType.create({
                    id,
                    pageId: SWInstancePageStateRecordType.parseId(id)
                })));
        }
        if (missingCameraIds.size > 0) {
            store.put([
                ...missingCameraIds
            ].map((id)=>CameraRecordType.create({
                    id
                })));
        }
    };
    return ensureStoreIsUsable;
}
